/**
 * Back in stock via Klaviyo.
 *
 * Två anrop, båda klientsidiga:
 *   1. /client/profiles/  - sätter locale och market som profilegenskaper.
 *                           Back-in-stock-endpointen accepterar INTE 'properties'
 *                           på profilen (svarar 400 invalid), därför separat.
 *   2. /client/back-in-stock-subscriptions/ - själva bevakningen. Svarar 202 och
 *                           genererar eventet "Subscribed to Back in Stock".
 *
 * Anrop 1 är icke-blockerande. Misslyckas det ska bevakningen ändå gå igenom.
 *
 * Två olika DOM-källor används med flit:
 *   ID    - temats dolda fält i köpformuläret, pekar på vald variant även
 *           när den är slutsåld
 *   TITEL - [data-selected-value] i variantväljaren
 *
 * [data-selected-variant] används INTE för ID. Den renderas som
 * selected_or_first_available_variant och pekar på en variant i lager.
 *
 * Dokumentation: https://developers.klaviyo.com/en/docs/how_to_set_up_custom_back_in_stock
 */
(function () {
  'use strict';

  var BIS_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var PROFILE_ENDPOINT = 'https://a.klaviyo.com/client/profiles/';
  var REVISION = '2024-06-15';

  if (customElements.get('klaviyo-bis')) return;

  customElements.define(
    'klaviyo-bis',
    class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-notification-form]');
        this.email = this.querySelector('[data-notification-email]');
        this.button = this.querySelector('[data-notification-submit]');
        this.fields = this.querySelector('[data-notification-fields]');
        this.message = this.querySelector('[data-notification-message]');
        this.messageText = this.querySelector('[data-notification-message-text]');
        this.messageVariant = this.querySelector('[data-notification-message-variant]');
        this.variantLabel = this.querySelector('[data-variant-title]');

        if (!this.form) return;

        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.sync();
        this.watch();
      }

      get productRoot() {
        return this.closest('product-info') || document;
      }

      watch() {
        var resync = this.sync.bind(this);

        var dialog = this.closest('dialog');
        if (dialog && typeof MutationObserver !== 'undefined') {
          new MutationObserver(function () {
            if (dialog.hasAttribute('open')) {
              resync();
              setTimeout(resync, 50);
              setTimeout(resync, 250);
            }
          }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
        }

        var popup = this.closest('popup-component');
        if (popup) {
          popup.addEventListener('click', function () {
            setTimeout(resync, 50);
          });
        }

        var selects = this.productRoot.querySelector('variant-selects');
        if (selects && typeof MutationObserver !== 'undefined') {
          new MutationObserver(resync).observe(selects, {
            childList: true,
            subtree: true,
            characterData: true
          });
        }

        window.addEventListener('popstate', resync);
      }

      /** Titeln byggs av valda alternativvärden: "Färg / Storlek". */
      get variantTitle() {
        var selects = this.productRoot.querySelector('variant-selects');
        if (selects) {
          var values = Array.prototype.slice
            .call(selects.querySelectorAll('[data-selected-value]'))
            .map(function (el) {
              return (el.textContent || '').trim();
            })
            .filter(Boolean);
          if (values.length) return values.join(' / ');
        }
        return this.variantLabel ? this.variantLabel.textContent.trim() : '';
      }

      get variantId() {
        var live = this.productRoot.querySelector('input.product-variant-id[name="id"]');
        if (live && live.value) return String(live.value);

        try {
          var fromUrl = new URLSearchParams(window.location.search).get('variant');
          if (fromUrl) return fromUrl;
        } catch (e) {
          /* ignorera */
        }

        return this.dataset.variantId;
      }

      sync() {
        if (!this.variantLabel) return;
        var title = this.variantTitle;
        if (title && this.variantLabel.textContent.trim() !== title) {
          this.variantLabel.textContent = title;
        }
      }

      get profileProperties() {
        var props = {};
        if (this.dataset.locale) props.locale = this.dataset.locale;
        if (this.dataset.market) props.market = this.dataset.market;
        return props;
      }

      headers() {
        return { 'content-type': 'application/json', revision: REVISION };
      }

      /**
       * Sätter språk och marknad på profilen. Icke-blockerande - ett fel här
       * får inte hindra själva bevakningen.
       */
      saveProfileProperties(email, companyId) {
        var props = this.profileProperties;
        if (!Object.keys(props).length) return Promise.resolve();

        return fetch(PROFILE_ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          headers: this.headers(),
          body: JSON.stringify({
            data: {
              type: 'profile',
              attributes: { email: email, properties: props }
            }
          })
        })
          .then(function (response) {
            if (response.status >= 400) {
              response.text().then(function (body) {
                console.warn('[Back in stock] Kunde inte spara profilegenskaper', response.status, body);
              });
            }
          })
          .catch(function (err) {
            console.warn('[Back in stock] Nätverksfel vid profilegenskaper', err);
          });
      }

      onSubmit(event) {
        event.preventDefault();

        if (!this.email || !this.email.checkValidity()) {
          if (this.email) this.email.reportValidity();
          return;
        }

        var variantId = this.variantId;
        var variantTitle = this.variantTitle;
        var companyId = this.dataset.companyId;
        var email = this.email.value.trim();

        if (!variantId || !companyId) {
          this.showMessage(this.dataset.errorText, false, '');
          return;
        }

        this.setBusy(true);

        var self = this;

        this.saveProfileProperties(email, companyId).then(function () {
          return fetch(BIS_ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
            method: 'POST',
            headers: self.headers(),
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  profile: { data: { type: 'profile', attributes: { email: email } } },
                  channels: ['EMAIL']
                },
                relationships: {
                  variant: {
                    data: {
                      type: 'catalog-variant',
                      id: '$shopify:::$default:::' + variantId
                    }
                  }
                }
              }
            })
          })
            .then(function (response) {
              self.setBusy(false);

              if (response.status === 202) {
                self.showMessage(self.dataset.successText, true, variantTitle);
                return;
              }

              response.text().then(function (body) {
                console.warn(
                  '[Back in stock] Klaviyo svarade ' + response.status +
                    ' för variant ' + variantId + ' (' + variantTitle + ')',
                  body
                );
              });

              self.showMessage(self.dataset.errorText, false, '');
            })
            .catch(function (err) {
              self.setBusy(false);
              console.warn('[Back in stock] Nätverksfel mot Klaviyo', err);
              self.showMessage(self.dataset.errorText, false, '');
            });
        });
      }

      setBusy(busy) {
        this.classList.toggle('is-loading', !!busy);
        if (!this.button) return;
        this.button.disabled = !!busy;
        this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
      }

      showMessage(text, success, variantTitle) {
        if (!this.message || !this.messageText) return;

        this.messageText.textContent = text || '';

        if (this.messageVariant) {
          var showVariant = !!(success && variantTitle);
          this.messageVariant.textContent = showVariant ? variantTitle : '';
          this.messageVariant.hidden = !showVariant;
        }

        this.message.hidden = false;
        this.message.classList.toggle('bis__status--success', !!success);
        this.message.classList.toggle('bis__status--error', !success);
        this.message.setAttribute('role', success ? 'status' : 'alert');

        if (success && this.fields) this.fields.hidden = true;

        if (typeof this.message.scrollIntoView === 'function') {
          this.message.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
    }
  );
})();
